import { useEffect, useMemo, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusPill, Section8Badge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet, Plus, CheckCircle2, Trash2, ShieldCheck } from "lucide-react";
import { ChargesPanel } from "@/components/ChargesPanel";
import { ReceiptButton } from "@/components/ReceiptButton";
import { toast } from "sonner";

const METHODS = ["zelle", "cashapp", "chase", "cash", "other"];

function RecordDialog({ leases, open, setOpen, onDone, preset }) {
  const [form, setForm] = useState({ lease_id: "", period: "", amount: "", source: "tenant", method: "zelle", reference: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);

  const presetLease = preset?.lease_id;
  const firstLease = leases[0]?.id || "";
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setForm({ lease_id: presetLease || firstLease, period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`, amount: "", source: "tenant", method: "zelle", reference: "", note: "" });
  }, [open, presetLease, firstLease]);

  const save = async () => {
    if (!form.lease_id || !form.amount) return toast.error("Lease and amount are required");
    setSaving(true);
    try {
      const { data } = await api.post("/payments", { ...form, amount: Number(form.amount) });
      const r = data.receipt;
      if (r?.sent) toast.success(`Payment recorded & confirmed · receipt emailed to ${r.to}`);
      else if (r && !r.skipped) toast.warning(`Payment recorded — receipt email could not be sent${r.error ? ` (${r.error})` : ""}`);
      else toast.success("Payment recorded & confirmed");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Record a payment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Lease</Label>
            <Select value={form.lease_id} onValueChange={(v) => set("lease_id", v)}>
              <SelectTrigger data-testid="record-lease"><SelectValue placeholder="Select lease" /></SelectTrigger>
              <SelectContent>
                {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant?.name} · {l.property?.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Period</Label><Input type="month" value={form.period} onChange={(e) => set("period", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="record-amount" placeholder="400" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">Tenant copay</SelectItem>
                  <SelectItem value="section8">Section 8 (HAP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => set("method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Reference / memo</Label><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Confirmation #" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="record-save" className="w-full">{saving ? "Saving…" : "Record payment"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Ledger({ leaseId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (leaseId) api.get(`/leases/${leaseId}/ledger`).then((r) => setData(r.data)); }, [leaseId, refreshKey]);
  if (!data) return <Loading />;
  const l = data.lease;
  const hasFees = data.ledger.some((r) => r.fees > 0);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-display font-bold text-slate-900">{l.property?.name}</h3>
        <Section8Badge section8={l.section8} />
        <span className="text-sm text-slate-500">{l.tenant?.name}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-slate-200 p-4"><div className="text-xs font-semibold uppercase text-slate-500">Total rent</div><div className="text-xl font-bold font-display mt-1">{fmtMoney(l.total_rent)}</div></div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-4"><div className="text-xs font-semibold uppercase text-amber-700">HAP voucher</div><div className="text-xl font-bold font-display text-amber-900 mt-1">{fmtMoney(l.hap_amount)}</div></div>
        <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4"><div className="text-xs font-semibold uppercase text-indigo-700">Tenant copay</div><div className="text-xl font-bold font-display text-indigo-900 mt-1">{fmtMoney(l.tenant_portion)}</div></div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Month</th>
              <th className="text-right font-semibold px-4 py-3">Tenant paid / due</th>
              {hasFees && <th className="text-right font-semibold px-4 py-3">Late fees</th>}
              {l.section8 && <th className="text-right font-semibold px-4 py-3">HAP paid / due</th>}
              <th className="text-right font-semibold px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.ledger.map((r) => (
              <tr key={r.period} data-testid={`ledger-row-${r.period}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{r.label}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">
                  <span className="text-slate-900 font-semibold">{fmtMoney(r.tenant_paid)}</span> <span className="text-slate-400">/ {fmtMoney(r.tenant_due)}</span>
                </td>
                {hasFees && (
                  <td className="px-4 py-3 text-right font-mono text-xs" data-testid={`ledger-fees-${r.period}`}>
                    {r.fees > 0 ? <span className="text-rose-700 font-semibold">+{fmtMoney(r.fees)}</span> : <span className="text-slate-300">—</span>}
                  </td>
                )}
                {l.section8 && (
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    <span className="text-slate-900 font-semibold">{fmtMoney(r.hap_paid)}</span> <span className="text-slate-400">/ {fmtMoney(r.hap_due)}</span>
                  </td>
                )}
                <td className="px-4 py-3 text-right"><StatusPill status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Payments() {
  const [leases, setLeases] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState("");
  const [recordOpen, setRecordOpen] = useState(false);
  const [ledgerKey, setLedgerKey] = useState(0);

  const load = () => {
    api.get("/leases").then((r) => {
      const active = r.data.filter((l) => l.status === "active");
      setLeases(active);
      if (active.length && !selected) setSelected(active[0].id);
    });
    api.get("/payments").then((r) => setPayments(r.data));
    setLedgerKey((k) => k + 1);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const recordPreset = useMemo(() => ({ lease_id: selected }), [selected]);

  const confirm = async (id) => {
    try {
      const { data } = await api.put(`/payments/${id}/confirm`);
      const r = data.receipt;
      if (r?.sent) toast.success(`Payment confirmed · receipt emailed to ${r.to}`);
      else if (r && !r.skipped) toast.warning(`Payment confirmed — receipt email could not be sent${r.error ? ` (${r.error})` : ""}`);
      else toast.success("Payment confirmed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { await api.delete(`/payments/${id}`); toast.success("Payment removed"); load(); };

  if (!leases) return <Loading />;
  const pending = payments.filter((p) => p.status === "pending");

  return (
    <div>
      <PageHeader title="Rent & Section 8" subtitle="Monthly ledgers, HAP vs tenant copay, and payment confirmations."
        action={<Button className="gap-2" onClick={() => setRecordOpen(true)} data-testid="record-payment-btn"><Plus className="h-4 w-4" /> Record payment</Button>}
      />

      {leases.length === 0 ? (
        <EmptyState icon={Wallet} title="No active leases" subtitle="Add a tenant with a lease to start tracking rent." />
      ) : (
        <Tabs defaultValue="ledger">
          <TabsList className="mb-5">
            <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Confirm payments {pending.length > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5">{pending.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All payments</TabsTrigger>
          </TabsList>

          <TabsContent value="ledger">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="mb-5 max-w-sm">
                <Label className="text-xs">Select lease</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger data-testid="ledger-lease-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant?.name} · {l.property?.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {selected && <Ledger leaseId={selected} refreshKey={ledgerKey} />}
              {selected && <ChargesPanel leaseId={selected} onChange={() => setLedgerKey((k) => k + 1)} />}
            </div>
          </TabsContent>

          <TabsContent value="pending">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              {pending.length === 0 ? (
                <div className="text-center py-10 text-slate-500"><ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-500" />All payments confirmed.</div>
              ) : (
                <div className="space-y-3">
                  {pending.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`pending-${p.id}`}>
                      <div>
                        <div className="font-semibold text-slate-900">{p.tenant_name} · {fmtMoney(p.amount)}</div>
                        <div className="text-xs text-slate-500 capitalize">{p.method} · {p.property_name} · {p.period} · reported by {p.created_by}</div>
                        {p.reference && <div className="text-xs text-slate-400 mt-0.5">Ref: {p.reference}</div>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="gap-1.5" onClick={() => confirm(p.id)} data-testid={`confirm-${p.id}`}><CheckCircle2 className="h-4 w-4" /> Confirm</Button>
                        <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="all">
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Tenant</th>
                      <th className="text-left font-semibold px-4 py-3">Period</th>
                      <th className="text-left font-semibold px-4 py-3">Source</th>
                      <th className="text-left font-semibold px-4 py-3">Method</th>
                      <th className="text-right font-semibold px-4 py-3">Amount</th>
                      <th className="text-right font-semibold px-4 py-3">Status</th>
                      <th className="text-right font-semibold px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{p.tenant_name}</td>
                        <td className="px-4 py-3 text-slate-600">{p.period}</td>
                        <td className="px-4 py-3">{p.source === "section8" ? <span className="text-amber-700 font-medium">HAP</span> : <span className="text-indigo-700 font-medium">Tenant</span>}</td>
                        <td className="px-4 py-3 capitalize text-slate-600">{p.method}</td>
                        <td className="px-4 py-3 text-right font-semibold font-mono">{fmtMoney(p.amount)}</td>
                        <td className="px-4 py-3 text-right"><StatusPill status={p.status} /></td>
                        <td className="px-2 py-2 text-right"><ReceiptButton payment={p} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <RecordDialog leases={leases} open={recordOpen} setOpen={setRecordOpen} onDone={load} preset={recordPreset} />
    </div>
  );
}
